// Cliente fetch das functions da API (same-origin /api).

export interface UserMin {
  id: string;
  email: string;
  name: string;
}

export interface CompanyMin {
  id: string;
  name: string;
  slug: string;
}

export interface TurmaMin {
  id: string;
  name: string;
  code: string | null;
}

export interface OfferingRow {
  offeringId: string;
  turmaId: string;
  turmaName: string;
  disciplinaId: string;
  disciplinaName: string;
  disciplinaCode: string | null;
  disciplinaStatus: "draft" | "active" | "closed";
}

export interface UpcomingAssessment {
  id: string;
  kind: "prova" | "trabalho" | "simulada";
  title: string;
  status: "scheduled" | "running" | "closed";
  startsAt: string | null;
  endsAt: string | null;
  durationMinutes: number | null;
  offeringId: string | null;
  attemptId: string | null;
  attemptStartedAt: string | null;
  attemptSubmittedAt: string | null;
}

// Banco "resumido" pra exibição na home (subset do BankRow completo).
export interface HomeBank {
  id: string;
  name: string;
  description: string | null;
  questionCount: number;
  /** Dissertativas paralelas (gerada por IA). 0 = não tem versão. */
  dissertativeCount?: number;
  /** Tipos de questão presentes — espelha o backend. ["mc"] / ["essay"] / ["mc","essay"]. */
  questionKinds?: Array<"mc" | "essay">;
  questionKindCounts?: { mc: number; essay: number };
  estudo: {
    respondidas: number;
    acertos: number;
    accuracyPct: number | null;
  };
  provas: {
    total: number;
    melhorPercentual: number | null;
    emAndamento: boolean;
  };
  lastActivityAt: string | null;
  /** Nome de quem compartilhou esse banco (null = é seu / via offering). */
  sharedByName?: string | null;
}

// Prova individual referenciando seu banco (ativa ou histórica)
export interface ProvaWithBank {
  id: string | null;
  bankId: string;
  bankName: string;
  iniciadaEm: number | null;
  encerradaEm: number | null;
  respondidas: number;
  acertos: number;
  percentualAcerto: number | null;
  duracaoMs: number;
  total: number;
}

export interface StudentHomeResponse {
  user: UserMin;
  company: CompanyMin;
  turmas: TurmaMin[];
  offerings: OfferingRow[];
  upcoming: UpcomingAssessment[];
  banks: HomeBank[];
  provasAtivas: ProvaWithBank[];
  provasHistorico: ProvaWithBank[];
  totals: {
    banks: number;
    provasHistorico: number;
  };
  paymentMethods?: {
    pixEnabled: boolean;
    ccEnabled: boolean;
    ccProvider: "pix_manual" | "stripe" | "mercadopago" | "mock";
  };
}

export type BankSource =
  | { kind: "public" }
  | { kind: "disciplina"; disciplinaId: string; disciplinaName: string };

export interface BankRow {
  id: string;
  name: string;
  description: string | null;
  questionCount: number;
  /** Tamanho do payload do banco em bytes (octet_length no servidor). Usado
   *  pelo modal de mesclar bancos pra avisar antes de estourar o cap do Neon. */
  sizeBytes?: number;
  /** Dissertativas paralelas (geradas por IA). 0 = não tem versão. */
  dissertativeCount?: number;
  /** Tipos de questão presentes — espelha o backend. ["mc"] / ["essay"] / ["mc","essay"]. */
  questionKinds?: Array<"mc" | "essay">;
  questionKindCounts?: { mc: number; essay: number };
  isPublic: boolean;
  sources: BankSource[];
  estudo: {
    respondidas: number;
    acertos: number;
    accuracyPct: number | null;
  };
  provas: {
    total: number;
    melhorPercentual: number | null;
    emAndamento: boolean;
  };
  lastActivityAt: string | null;
  /** Quem compartilhou esse banco (null = é do aluno ou via offering). */
  sharedByName?: string | null;
  /** Pasta pessoal do aluno onde este banco está (null = "Sem pasta"). */
  folderId?: string | null;
  /** Quando o banco foi importado da Biblioteca de Provas, ID do banco
   *  original (público). Usado pelo menu pra mostrar "(reimportável)". */
  importedFromLibraryBankId?: string | null;
  /** True quando o aluno pode tirar esse banco do acervo (cópia da
   *  biblioteca OU grant compartilhado). Banco próprio e de disciplina
   *  ficam false. */
  canRemoveFromAcervo?: boolean;
}

export interface StudentBanksResponse {
  banks: BankRow[];
}

export interface ProvaResumo {
  id: string | null;
  iniciadaEm: number | null;
  encerradaEm: number | null;
  respondidas: number;
  acertos: number;
  percentualAcerto: number | null;
  duracaoMs: number;
  total: number;
}

export interface BankDetailResponse {
  bank: {
    id: string;
    legacyId: string | null;
    name: string;
    questionCount: number;
    /** Aluno é o dono do banco. Habilita botão "Compartilhar". */
    isOwner: boolean;
    /** Quem compartilhou esse banco com o aluno (null = é dele OU veio
     *  via offering/professor). */
    sharedByName: string | null;
    /** Quantas questões dissertativas existem no banco (versão paralela MC).
     *  0 = sem versão dissertativa. Indica disponibilidade. */
    dissertativeCount: number;
    /** Quando o banco foi gerado por "Gerar simulado a partir do caderno",
     *  carrega ref pra origem renderizar badge + link de volta. */
    generatedFromCaderno?: {
      sessionId: string;
      sessionTitle: string;
      generatedAt: string | null;
    } | null;
  };
  dissertativaEstudo?: {
    respondidas: number;
    avaliadas: number;
    scoreMedia: number | null;
  };
  dissertativaProva?: {
    respondidas: number;
    avaliadas: number;
    scoreMedia: number | null;
    finalizadaEm: string | null;
  };
  estudo: {
    respondidas: number;
    acertos: number;
    percentualAcerto: number | null;
    temasFiltrados: number;
    fontesFiltradas: number;
  };
  provaAtiva: (ProvaResumo & { decorridoMs: number }) | null;
  provasHistorico: ProvaResumo[];
  lastActivityAt: string | null;
}

/** Item de alternativa — string OU objeto com imagem. Permite questões com
 *  alternativas-imagem (matriz visual, diagrama, mapa) sem forçar tudo no
 *  enunciado. Render detecta o tipo em runtime. */
export type OpcaoItem =
  | string
  | { imagem: string; legenda?: string };

// Payload completo do banco + estado de estudo, pra alimentar o workspace
// de Modo Estudo no painel novo.
export interface QuestaoStudy {
  id: string;
  pergunta: string;
  opcoes: OpcaoItem[];
  /** Alias legado de `opcoes` — bancos antigos ainda usam esse nome. */
  alternativas?: OpcaoItem[];
  correta: number;
  /** Tipo da questão. "mc" = múltipla escolha (default), "essay" = dissertativa. */
  tipo?: "mc" | "essay";
  explicacao?: string;
  /** Alias legado de `explicacao` — bancos antigos ainda usam esse nome. */
  resolucao?: string;
  caso?: string;
  imagens?: Array<string | { url: string; legenda?: string }>;
  imagem_url?: string;
  tabelas?: unknown[];
  tema?: string;
  subtema?: string;
  fonte?: string;
  /** Documento/arquivo de origem (banco gerado por IA a partir de PDFs). */
  arquivo_origem?: string;
  dificuldade?: number;
  numero_original?: string | number;
  numero_origem?: string | number;
  // Campos de questões dissertativas (espelham prompt-extracao).
  banca?: string;
  genero?: string;
  linhas_min?: number;
  linhas_max?: number;
  fragmentos?: Array<{ label: string; textContent: string }>;
  direcionadores?: string;
  gabarito_modelo?: string;
  criterios?: string[];
  competencias?: string[];
  nota_maxima?: number;
  notaQuestao?: number;
  /** Sub-itens (questão composta — caso/imagens/tabelas/fragmentos vivem no
   *  pai, cada item carrega seu próprio comando/tipo/gabarito). Quando
   *  ausente ou com 0/1 elementos, a questão se comporta como simples. */
  items?: QuestaoItem[];
}

/** Sub-item de uma questão composta — espelha `EditItem` do editor. */
export interface QuestaoItem {
  id: string;
  label: string;
  pergunta: string;
  tipo?: "mc" | "essay";
  opcoes?: OpcaoItem[];
  correta?: number;
  explicacao?: string;
  banca?: string;
  genero?: string;
  linhas_min?: number;
  linhas_max?: number;
  direcionadores?: string;
  gabarito_modelo?: string;
  criterios?: string[];
  competencias?: string[];
  nota_maxima?: number;
  notaQuestao?: number;
}

export type QuestionKind = "mc" | "essay";

export interface EssayResponseState {
  resposta: string;
  nota: number | null;
  feedback: string | null;
  status: "EXCELENTE" | "BOM" | "REGULAR" | "INSUFICIENTE" | null;
  gradedAt: string | null;
  provider: string | null;
  model: string | null;
  /** Modo de correção usado nesta correção (Padrão/Rigoroso).
   *  Salvo apenas em Estudo/Simulação — em Prova Agendada fica null. */
  strictnessUsed?: "standard" | "strict" | null;
  /** Quando o desconto de norma culta esteve ativo. Só vale pra dissertativa
   *  (não redação). Null em qualquer outro caso. */
  descontoNormaCulta?: {
    pontos: number;
    erros: Array<{ trecho: string; problema: string; correto: string }>;
  } | null;
  /** Nota antes do desconto, quando houve desconto. */
  scoreSemDesconto?: number | null;
}

export interface BankStudyResponse {
  bank: {
    id: string;
    name: string;
    legacyId: string | null;
    questionCount: number;
  };
  questoes: QuestaoStudy[];
  estudo: {
    respostas: Record<string, number>;
    lastQid: string | null;
    agrupamento: "tema" | "origem" | null;
    filtros: {
      temas: string[];
      fontes: string[];
      dificuldade: number[];
      busca: string;
      somenteImagem: boolean;
      somenteCaso: boolean;
      somenteTabela: boolean;
      /** Tipos de questão a mostrar. Vazio = todos. */
      tipos: QuestionKind[];
      status: Array<"correct" | "wrong" | "pending">;
    };
    /** Respostas dissertativas (essay). Chave = item.id em composta, q.id
     *  em essay simples. Cada estado tem texto + última correção IA. */
    essayResponses?: Record<string, EssayResponseState>;
  };
  /** Histórico de conversas com o tutor IA por questão (qid). Persistido
   *  pelo backend; cliente hidrata o signal chatHistory ao abrir o banco. */
  aiChats?: Record<string, AiChatThread>;
}

export interface AiChatThread {
  history: Array<{ role: "user" | "assistant"; content: string }>;
  persona: AiPersona | null;
  updatedAt: number;
}

// =========================================================================
// Prova (não-agendada)
// =========================================================================

export interface ProvaConfig {
  // Tempo alvo total da prova em ms. 0 = sem limite (cronômetro só conta).
  tempoAlvoMs: number;
  // Se true, cronômetro regressivo (alvo → 0); senão progressivo (0 → ...).
  regressivo: boolean;
  // Mostra cronômetro geral no header (e por questão, se ligado).
  mostrarRelogioGeral: boolean;
  mostrarRelogioQuestao: boolean;
  // Mostra "Por questão": tempo restante ÷ não respondidas. Sem tempo
  // máximo definido → exibe "Não Definido". Atualiza a cada tick (500ms).
  mostrarRelogioRitmo?: boolean;
  // Esconde tema/dificuldade pra simular ambiente de prova "cego".
  ocultarTema: boolean;
  ocultarDificuldade: boolean;
  // Definição do escopo. "todas" inclui o banco inteiro; "personalizar"
  // filtra por tema/fonte/dificuldade; "numeros" usa lista de índices 1-based.
  escopo:
    | { tipo: "todas" }
    | {
        tipo: "personalizar";
        temas: string[];
        fontes: string[];
        dificuldade: number[];
      }
    | { tipo: "numeros"; numerosRaw: string };
}

// Estado em andamento (matches sanitizeProva no backend).
export interface ProvaState {
  id: string;
  iniciadaEm: number;
  encerradaEm: number | null;
  config: ProvaConfig;
  escopoQuestaoIds: string[];
  respostas: Record<
    string,
    { opcao: number; tempoMs: number; respondidaEm: number | null }
  >;
  tempoTotalMs: number;
  tempoExtraMs: number;
  tempoPorQuestao: Record<string, number>;
  questaoAtualId: string | null;
  questaoEntrouEm: number | null;
  ultimoRetomadaEm: number;
  pausada: boolean;
  motivoEncerramento?: string;
  /** Chaves de essayResponses (qid OU itemId) que foram editadas DURANTE
   *  esta prova. Sem isso, o counter contava respostas de Study mode que
   *  vivem na MESMA bucket (estudo.essayResponses). Pedido Karol 2026-05-24:
   *  "prova deve iniciar sem memória, não misturar progresso de Study". */
  essayKeysTouched?: string[];
  /** Bucket de respostas dissertativas SCOPADA à prova. Separada da
   *  estudo.essayResponses pra não ter overwrite destrutivo entre Study e
   *  Prova. Nasce vazia em criarProvaState. ResultView/RevisaoView leem
   *  daqui (e fallback estudo se vazio, pra provas legadas pré-refactor).
   *  Pedido Karol 2026-05-24: "não podemos misturar progresso". */
  essayResponses?: Record<string, EssayResponseState>;
}

// Resumo (stats finais) — mesmo shape de ProvaResumo em bank-detail.
export interface ProvaResumoEnd {
  id: string | null;
  iniciadaEm: number | null;
  encerradaEm: number | null;
  respondidas: number;
  acertos: number;
  percentualAcerto: number | null;
  duracaoMs: number;
  total: number;
}

export interface BankProvaResponse {
  bank: { id: string; name: string; questionCount: number };
  questoes: QuestaoStudy[];
  provaAtiva: ProvaState | null;
  /** Respostas dissertativas hidratadas do bucket estudo (mesmo bucket
   *  usado pelo save/grade de essay — prova compartilha por enquanto). */
  essayResponses?: Record<string, EssayResponseState>;
}

export interface FaqStudentEntry {
  id: string;
  question: string;
  answer: string;
  category: string | null;
  categoryId: string | null;
}

export interface FaqStudentCategory {
  id: string;
  name: string;
  slug: string;
  parentId: string | null;
  icon: string | null;
  position: number;
}

export type UserReportKind = "bug" | "feature" | "bank_request" | "feedback" | "suporte";
export type UserReportStatus = "open" | "in_progress" | "resolved" | "wontfix";
export type UserReportSeverity = "low" | "medium" | "high" | "critical";

export interface UserReportRow {
  id: string;
  kind: UserReportKind;
  title: string;
  description: string | null;
  status: UserReportStatus;
  severity: UserReportSeverity | null;
  /** 1-5 estrelas. NULL pra kinds que não são "feedback". */
  rating: number | null;
  adminResponse: string | null;
  adminName: string | null;
  respondedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export class ApiError extends Error {
  /** Body completo retornado pelo backend no caso de erro. Útil quando
   *  o endpoint embute payload junto com o erro (ex: QuickStudy retorna
   *  userMessage + assistantMessage placeholder mesmo em status 502/402). */
  public body: unknown;
  constructor(
    public code: string,
    message: string,
    public status: number,
    body?: unknown
  ) {
    super(message);
    this.body = body;
  }
}

export async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: init.body ? { "content-type": "application/json" } : undefined,
    ...init
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      body?.error?.code ?? `http_${res.status}`,
      body?.error?.message ?? `Erro ${res.status}`,
      res.status,
      body
    );
  }
  return res.json() as Promise<T>;
}

/** Mescla vários bancos do aluno num banco novo, na ordem informada.
 *  Originais ficam intactos; duplicadas são removidas por padrão. */
export async function mergeBanks(args: {
  bankIds: string[];
  name: string;
  description?: string;
  dedupe?: boolean;
}): Promise<{ bank: { id: string; name: string }; total: number; deduped: number }> {
  return call("/api/student/banks/merge", {
    method: "POST",
    body: JSON.stringify(args)
  });
}

export interface FetchProgress {
  /** bytes recebidos (já descompactado pelo browser quando vem gzip). */
  loaded: number;
  /** total esperado em bytes (do header X-Uncompressed-Length quando existe,
   *  senão Content-Length quando existe, senão null = indeterminado). */
  total: number | null;
}

// Versão de fetch que avisa progresso de download — pra endpoints grandes
// (ex: bank-prova com ~10MB). Browser entrega body já descompactado quando
// o response é gzipped, então loaded vai bater com X-Uncompressed-Length.
export async function callWithProgress<T>(
  path: string,
  onProgress: (p: FetchProgress) => void,
  init: RequestInit = {}
): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: init.body ? { "content-type": "application/json" } : undefined,
    ...init
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      body?.error?.code ?? `http_${res.status}`,
      body?.error?.message ?? `Erro ${res.status}`,
      res.status
    );
  }
  const uncompressed = res.headers.get("x-uncompressed-length");
  const contentLen = res.headers.get("content-length");
  const total = uncompressed
    ? Number(uncompressed)
    : contentLen
      ? Number(contentLen)
      : null;
  if (!res.body) {
    return res.json() as Promise<T>;
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  onProgress({ loaded: 0, total });
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      loaded += value.byteLength;
      onProgress({ loaded, total });
    }
  }
  // Junta chunks num único Uint8Array e decodifica
  const totalLen = chunks.reduce((acc, c) => acc + c.byteLength, 0);
  const merged = new Uint8Array(totalLen);
  let off = 0;
  for (const c of chunks) {
    merged.set(c, off);
    off += c.byteLength;
  }
  const text = new TextDecoder("utf-8").decode(merged);
  return JSON.parse(text) as T;
}

export interface ApiTokenRow {
  id: string;
  label: string;
  prefix: string;
  scope: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export const api = {
  home: () => call<StudentHomeResponse>("/api/student/home"),
  referral: () =>
    call<{
      code: string;
      link: string;
      totalReferred: number;
      totalActive: number;
      indicated: Array<{
        displayName: string;
        createdAtMonth: string;
        suspended: boolean;
        active: boolean;
      }>;
    }>("/api/student/referral"),
  me: () =>
    call<{
      user: { id: string; name: string; email: string };
      impersonating: {
        actorId: string;
        actorName: string | null;
        startedAt: string;
      } | null;
    }>("/api/auth/me"),
  impersonateStop: () =>
    call<{ ok: true }>("/api/manager/impersonate-stop", { method: "POST" }),
  banks: () => call<StudentBanksResponse>("/api/student/banks"),
  bankDetail: (bankId: string) =>
    call<BankDetailResponse>(
      `/api/student/bank-detail?bankId=${encodeURIComponent(bankId)}`
    ),
  bankStudy: (bankId: string) =>
    call<BankStudyResponse>(
      `/api/student/bank-study?bankId=${encodeURIComponent(bankId)}`
    ),
  saveBankProgress: (
    bankId: string,
    estudo: BankStudyResponse["estudo"]
  ) =>
    call<{ ok: true; savedAt: string }>(`/api/student/bank-progress`, {
      method: "PUT",
      body: JSON.stringify({ bankId, estudo })
    }),
  saveEssayResponse: (bankId: string, qid: string, itemId: string | null, resposta: string) => {
    const qs = new URLSearchParams({ bankId, qid });
    if (itemId) qs.set("itemId", itemId);
    return call<{ ok: true; savedAt: string }>(
      `/api/student/bank-essay?${qs.toString()}`,
      { method: "PATCH", body: JSON.stringify({ resposta }) }
    );
  },
  gradeEssayResponse: (
    bankId: string,
    qid: string,
    itemId: string | null,
    resposta?: string
  ) => {
    const qs = new URLSearchParams({ bankId, qid, action: "grade" });
    if (itemId) qs.set("itemId", itemId);
    return call<{ ok: true; grade: EssayResponseState }>(
      `/api/student/bank-essay?${qs.toString()}`,
      {
        method: "POST",
        body: JSON.stringify(resposta != null ? { resposta } : {})
      }
    );
  },
  clearEssayResponse: (bankId: string, qid: string, itemId: string | null) => {
    const qs = new URLSearchParams({ bankId, qid });
    if (itemId) qs.set("itemId", itemId);
    return call<{ ok: true; cleared: true }>(
      `/api/student/bank-essay?${qs.toString()}`,
      { method: "DELETE" }
    );
  },
  resetBankProgress: (bankId: string) =>
    call<{ ok: true; mode: "all"; clearedAt: string }>(
      `/api/student/bank-progress?bankId=${encodeURIComponent(bankId)}`,
      { method: "DELETE" }
    ),
  clearProgressForKeys: (bankId: string, keys: string[]) => {
    const qs = new URLSearchParams({ bankId, keys: keys.join(",") });
    return call<{ ok: true; mode: "keys"; cleared: number; clearedAt: string }>(
      `/api/student/bank-progress?${qs.toString()}`,
      { method: "DELETE" }
    );
  },
  getPrefs: () => call<{ prefs: Record<string, unknown> | null }>("/api/student/prefs"),
  savePrefs: (prefs: Record<string, unknown>) =>
    call<{ ok: true }>("/api/student/prefs", {
      method: "PUT",
      body: JSON.stringify({ prefs })
    }),
  bankProva: (bankId: string) =>
    call<BankProvaResponse>(
      `/api/student/bank-prova?bankId=${encodeURIComponent(bankId)}`
    ),
  bankProvaWithProgress: (
    bankId: string,
    onProgress: (p: FetchProgress) => void
  ) =>
    callWithProgress<BankProvaResponse>(
      `/api/student/bank-prova?bankId=${encodeURIComponent(bankId)}`,
      onProgress
    ),
  saveProvaActive: (bankId: string, prova: ProvaState) =>
    call<{ ok: true; savedAt: string }>(`/api/student/bank-prova-active`, {
      method: "PUT",
      body: JSON.stringify({ bankId, prova })
    }),
  endProva: (
    bankId: string,
    prova: ProvaState,
    motivo: "manual" | "tempo" | "falta-respostas"
  ) =>
    call<{ ok: true; prova: ProvaState; resumo: ProvaResumoEnd }>(
      `/api/student/bank-prova-end`,
      {
        method: "POST",
        body: JSON.stringify({ bankId, prova, motivo })
      }
    ),
  // Detalhe COMPLETO de prova encerrada do histórico (respostas, tempo por
  // questão, escopo, config). Usado pelo ResultView quando a prova não veio
  // prefilled (entrou via URL).
  bankProvaDetail: (bankId: string, provaId: string) =>
    call<{
      bank: { id: string; name: string; questionCount: number };
      questoes: QuestaoStudy[];
      prova: ProvaState;
      essayResponses?: Record<string, EssayResponseState>;
    }>(
      `/api/student/bank-prova-detail?bankId=${encodeURIComponent(
        bankId
      )}&provaId=${encodeURIComponent(provaId)}`
    ),
  // Retry de grading IA pra essays/items da prova encerrada que ficaram
  // sem nota. Karol/Luís 2026-05-24 — encerramento parcial recuperável.
  bankProvaGradeRetry: (
    bankId: string,
    provaId: string,
    opts: { force?: boolean } = {}
  ) =>
    call<{
      ok: true;
      provaId: string;
      graded: number;
      failed: number;
      totalEssays: number;
      bucketSize: number;
    }>(
      `/api/student/bank-prova-grade-retry?bankId=${encodeURIComponent(
        bankId
      )}&provaId=${encodeURIComponent(provaId)}`,
      {
        method: "POST",
        body: JSON.stringify(opts)
      }
    ),
  // Soft delete (hide) de prova encerrada — marca hiddenByStudentAt e
  // some do histórico exibido. Manager pode auditar/restaurar.
  hideProva: (bankId: string, provaId: string) =>
    call<{ ok: true; provaId: string; hiddenByStudentAt: number }>(
      `/api/student/bank-prova-hide`,
      {
        method: "POST",
        body: JSON.stringify({ bankId, provaId })
      }
    ),
  logout: () => call<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  // ===== Disciplinas / Matérias =====
  listMyDisciplinas: () =>
    call<{ disciplinas: StudentDisciplinaRow[] }>("/api/student/disciplinas"),
  /** Link público de pagamento pra uma compra (gera lazily). */
  getChargeLink: (compraId: string) =>
    call<{ slug: string; url: string }>(
      `/api/charges/link?compraId=${encodeURIComponent(compraId)}`,
      { method: "POST" }
    ),
  getMyDisciplina: (id: string) =>
    call<{ disciplina: StudentDisciplinaDetail }>(
      `/api/student/disciplinas?id=${encodeURIComponent(id)}`
    ),
  // ===== Pacotes (catálogo + saldo) =====
  listPacotesDisciplina: (disciplinaId: string, offeringId?: string) => {
    const q = new URLSearchParams();
    q.set("disciplinaId", disciplinaId);
    if (offeringId) q.set("offeringId", offeringId);
    return call<{ pacotes: PacoteAvailable[]; saldo: number | null }>(
      `/api/student/pacotes?${q.toString()}`
    );
  },
  // ===== Compras =====
  listMyCompras: (status?: "pending" | "paid" | "all") => {
    const q = new URLSearchParams();
    if (status && status !== "all") q.set("status", status);
    return call<{ compras: CompraRow[] }>(`/api/student/compras?${q.toString()}`);
  },
  getCompra: (id: string) =>
    call<{ compra: CompraRow; company?: CompraCompanyBranding }>(
      `/api/student/compras?id=${encodeURIComponent(id)}`
    ),
  createCompra: (data: {
    offeringId: string;
    pacoteId: string;
    valorCents?: number;
    descricao?: string;
    /** Quando true, cria também uma assinatura recorrente (billing
     *  pix_monthly por padrão). Default false (compra avulsa). */
    recurring?: boolean;
  }) =>
    call<{
      compra: CompraRow;
      subscriptionId: string | null;
      nextChargeAt: string | null;
    }>("/api/student/compras", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  cancelCompra: (id: string) =>
    call<{ compra: CompraRow }>(
      `/api/student/compras?id=${encodeURIComponent(id)}&action=cancel`,
      { method: "POST" }
    ),
  payCompraWithCC: (
    id: string,
    data: {
      number: string;
      holderName: string;
      holderDoc: string;
      cvv: string;
      expMonth: number;
      expYear: number;
      billingAddress: {
        zip: string;
        street: string;
        number: string;
        complement?: string;
        neighborhood?: string;
        city: string;
        state: string;
      };
      /** Opcional — se a compra é o "mês 1" de uma assinatura criada como
       *  pix_monthly mas o aluno escolheu pagar com cartão, manda o
       *  subscriptionId pra trocar billingType pra credit_card. */
      subscriptionId?: string;
    }
  ) =>
    call<{
      ok: true;
      compra: CompraRow;
      card: { brand: string; last4: string };
    }>(`/api/student/compras?id=${encodeURIComponent(id)}&action=pay-cc`, {
      method: "POST",
      body: JSON.stringify(data)
    }),
  // ===== Notificações (cross-context, sessão basta) =====
  listNotifications: (opts: { onlyUnread?: boolean; includeHidden?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (opts.onlyUnread) q.set("onlyUnread", "1");
    if (opts.includeHidden) q.set("include", "hidden");
    const qs = q.toString();
    return call<{ notifications: NotificationRow[]; unreadCount: number }>(
      `/api/notifications${qs ? "?" + qs : ""}`
    );
  },
  markNotifRead: (id: string) =>
    call<{ notification: NotificationRow }>(
      `/api/notifications?id=${encodeURIComponent(id)}&action=read`,
      { method: "PATCH" }
    ),
  markNotifUnread: (id: string) =>
    call<{ notification: NotificationRow }>(
      `/api/notifications?id=${encodeURIComponent(id)}&action=unread`,
      { method: "PATCH" }
    ),
  hideNotif: (id: string) =>
    call<{ notification: NotificationRow }>(
      `/api/notifications?id=${encodeURIComponent(id)}&action=hide`,
      { method: "PATCH" }
    ),
  markAllNotifsRead: () =>
    call<{ ok: true }>(`/api/notifications?action=read-all`, {
      method: "PATCH"
    }),
  // ===== Minha Conta =====
  getAccount: () => call<{ user: AccountInfo }>("/api/user/account"),
  patchAccount: (data: {
    name?: string;
    socialName?: string | null;
    initialsOverride?: string | null;
    apelido?: string | null;
    phone?: string | null;
    prefs?: Record<string, unknown> | null;
  }) =>
    call<{ user: AccountInfo }>("/api/user/account", {
      method: "PATCH",
      body: JSON.stringify(data)
    }),
  // ===== Encerramento de conta — [[project-account-closure]] =====
  getAccountClosure: () =>
    call<{
      request: { id: string; status: string; requestedAt: string } | null;
      confirmPhrase: string;
    }>("/api/user/account-closure"),
  requestAccountClosure: (confirm: string, reason?: string) =>
    call<{ request: { id: string; status: string; requestedAt: string }; alreadyOpen?: boolean }>(
      "/api/user/account-closure",
      { method: "POST", body: JSON.stringify({ confirm, reason }) }
    ),
  cancelAccountClosure: () =>
    call<{ ok: true }>("/api/user/account-closure", { method: "DELETE" }),
  // ===== Tokens de API / extensão Chrome — [[project-chrome-extension-assistant]] =====
  getApiTokens: () =>
    call<{ allowed: boolean; tokens: ApiTokenRow[] }>("/api/user/api-tokens"),
  createApiToken: (label?: string) =>
    call<{
      token: string;
      id: string;
      label: string;
      prefix: string;
      expiresAt: string | null;
    }>("/api/user/api-tokens", {
      method: "POST",
      body: JSON.stringify({ label })
    }),
  revokeApiToken: (id: string) =>
    call<{ ok: true }>(`/api/user/api-tokens?id=${encodeURIComponent(id)}`, {
      method: "DELETE"
    }),
  // ===== Lembretes (prefs default) =====
  listReminderPrefs: () =>
    call<{ prefs: ReminderPref[] }>("/api/user/reminders"),
  createReminderPref: (data: { offsetMinutes: number; channel: ReminderChannel }) =>
    call<{ pref: ReminderPref }>("/api/user/reminders", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  deleteReminderPref: (id: string) =>
    call<{ deleted: string }>(
      `/api/user/reminders?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  // ===== Agenda (aulas do aluno) =====
  listMyAulas: (params: { from?: string; to?: string; offeringId?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.from) q.set("from", params.from);
    if (params.to) q.set("to", params.to);
    if (params.offeringId) q.set("offeringId", params.offeringId);
    const qs = q.toString();
    return call<{ aulas: AulaStudentRow[] }>(
      `/api/student/aulas${qs ? "?" + qs : ""}`
    );
  },
  // ===== Passkeys (biometria) =====
  passkeyRegisterOptions: () =>
    call<{ options: any; challengeId: string }>(
      "/api/auth/passkey/register-options",
      { method: "POST", body: "{}" }
    ),
  passkeyRegisterVerify: (data: {
    challengeId: string;
    credential: any;
    deviceLabel?: string;
    deviceMeta?: Record<string, string | undefined>;
  }) =>
    call<{ ok: true; user: AccountInfo | null }>(
      "/api/auth/passkey/register-verify",
      { method: "POST", body: JSON.stringify(data) }
    ),
  passkeyLoginOptions: (email: string) =>
    call<{ options: any; challengeId: string }>(
      "/api/auth/passkey/login-options",
      { method: "POST", body: JSON.stringify({ email }) }
    ),
  passkeyLoginVerify: (data: {
    email: string;
    challengeId: string;
    credential: any;
  }) =>
    call<{ user: AccountInfo; contexts: unknown[] }>(
      "/api/auth/passkey/login-verify",
      { method: "POST", body: JSON.stringify(data) }
    ),
  listPasskeys: () =>
    call<{ passkeys: PasskeyDevice[] }>("/api/user/passkeys"),
  renamePasskey: (id: string, name: string) =>
    call<{ ok: true }>(
      `/api/user/passkeys?id=${encodeURIComponent(id)}&action=rename`,
      { method: "PATCH", body: JSON.stringify({ name }) }
    ),
  deletePasskey: (id: string) =>
    call<{ deleted: string }>(
      `/api/user/passkeys?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  // ===== Mensagens =====
  listInbox: () =>
    call<{ messages: InboxMessageRow[]; unreadCount: number }>(
      "/api/student/messages"
    ),
  markMessageRead: (id: string) =>
    call<{ ok: true }>(
      `/api/student/messages?id=${encodeURIComponent(id)}&action=read`,
      { method: "PATCH" }
    ),
  markMessageUnread: (id: string) =>
    call<{ ok: true }>(
      `/api/student/messages?id=${encodeURIComponent(id)}&action=unread`,
      { method: "PATCH" }
    ),
  // ===== User reports (bug + feature request) =====
  listMyReports: () =>
    call<{ reports: UserReportRow[] }>("/api/student/user-reports"),
  getMyReport: (id: string) =>
    call<{ report: UserReportRow }>(
      `/api/student/user-reports?id=${encodeURIComponent(id)}`
    ),
  createReport: (input: {
    kind: UserReportKind;
    title: string;
    description?: string;
    rating?: number;
    context?: Record<string, unknown>;
  }) =>
    call<{ report: UserReportRow }>("/api/student/user-reports", {
      method: "POST",
      body: JSON.stringify(input)
    }),
  // ===== FAQ =====
  getFaq: () =>
    call<{
      faq: {
        isEnabled: boolean;
        entries: FaqStudentEntry[];
        categories: FaqStudentCategory[];
      };
    }>("/api/student/faq"),
  // ===== Conteúdos =====
  listContents: (params: { disciplinaId?: string; offeringId?: string }) => {
    const q = new URLSearchParams();
    if (params.disciplinaId) q.set("disciplinaId", params.disciplinaId);
    if (params.offeringId) q.set("offeringId", params.offeringId);
    return call<{ contents: ContentItem[] }>(
      `/api/student/contents?${q.toString()}`
    );
  },
  // Assistente IA do Modo Estudo. Envia a questão atual + histórico do chat
  // dessa questão + mensagem nova; servidor escolhe persona e chama o provedor configurado.
  aiChatClear: (bankId: string, qid: string) =>
    call<{ ok: true }>(
      `/api/student/ai-chat-clear?bankId=${encodeURIComponent(bankId)}&qid=${encodeURIComponent(qid)}`,
      { method: "POST" }
    ),
  aiChat: (payload: AiChatRequest) =>
    call<AiChatResponse>("/api/student/ai-chat", {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  // Ações inline do editor de anotações: explicar/resumir/melhorar/flashcards.
  // Sem persona, sem histórico — stateless e curto. [[project-notes-system-caderno]]
  aiQuickText: (payload: {
    action: "explain" | "summarize" | "improve" | "flashcards";
    text: string;
    context?: string;
  }) =>
    call<{ text: string }>("/api/student/ai-quick", {
      method: "POST",
      body: JSON.stringify(payload)
    }),
  aiCatalog: () => call<AiCatalogResponse>("/api/student/ai-catalog"),
  aiConnections: () => call<AiConnectionsResponse>("/api/student/ai-connections"),
  updateAiConnectionInterest: (
    provider: "gemini" | "openai" | "anthropic" | "xai" | "deepseek",
    interested: boolean
  ) =>
    call<AiConnectionsUpdateResponse>("/api/student/ai-connections", {
      method: "PATCH",
      body: JSON.stringify({ provider, interested })
    }),
  // Valida uma API key do provedor selecionado fazendo uma chamada pequena.
  // Usado no onboarding pra confirmar antes de salvar localmente.
  aiValidateKey: (payload: {
    provider: "gemini" | "openai" | "anthropic" | "xai" | "deepseek";
    apiKey: string;
    model?: string;
  }) =>
    call<AiValidateKeyResponse>(
      "/api/student/ai-validate-key",
      {
        method: "POST",
        body: JSON.stringify(payload)
      }
    ),
  // ===== Webhooks (do próprio usuário) =====
  listWebhooks: () =>
    call<{
      scope: "teacher" | "student";
      allowedEvents: string[];
      subscriptions: UserWebhookSubscription[];
      events: UserWebhookEventLog[];
    }>("/api/user/webhooks"),
  createWebhook: (data: { url: string; events?: string[]; description?: string }) =>
    call<{ subscription: UserWebhookSubscription; secret: string }>(
      "/api/user/webhooks",
      { method: "POST", body: JSON.stringify(data) }
    ),
  rotateWebhookSecret: (id: string) =>
    call<{ secret: string }>(
      `/api/user/webhooks?id=${encodeURIComponent(id)}&action=rotate-secret`,
      { method: "POST" }
    ),
  patchWebhook: (
    id: string,
    data: { url?: string; events?: string[]; active?: boolean; description?: string }
  ) =>
    call<{ subscription: UserWebhookSubscription }>(
      `/api/user/webhooks?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(data) }
    ),
  deleteWebhook: (id: string) =>
    call<{ ok: true }>(`/api/user/webhooks?id=${encodeURIComponent(id)}`, {
      method: "DELETE"
    }),
  // ===== Provas agendadas (assessments kind=prova) =====
  listScheduledExams: () =>
    call<{ exams: StudentExamRow[] }>("/api/student/exams"),
  getScheduledExam: (examId: string) =>
    call<StudentExamDetailResponse>(
      `/api/student/exam?examId=${encodeURIComponent(examId)}`
    ),
  saveScheduledExamActive: (
    examId: string,
    answers: Record<string, ScheduledExamAnswer>,
    currentIdx?: number
  ) =>
    call<{ ok: true; savedAt: string }>("/api/student/exam-active", {
      method: "PUT",
      body: JSON.stringify({ examId, answers, currentIdx })
    }),
  heartbeatScheduledExam: (examId: string) =>
    call<{ ok: true; at: string }>("/api/student/exam-heartbeat", {
      method: "PUT",
      body: JSON.stringify({ examId })
    }),
  pauseScheduledExam: (examId: string) =>
    call<{ ok: true; pauseMeta: ScheduledExamPauseMeta }>(
      `/api/student/exam-pause?examId=${encodeURIComponent(examId)}&action=pause`,
      { method: "POST", body: "{}" }
    ),
  resumeScheduledExam: (examId: string) =>
    call<{ ok: true; pauseMeta: ScheduledExamPauseMeta }>(
      `/api/student/exam-pause?examId=${encodeURIComponent(examId)}&action=resume`,
      { method: "POST", body: "{}" }
    ),
  endScheduledExam: (examId: string) =>
    call<{
      ok: true;
      score: number;
      acertos: number;
      totalQuestoes: number;
      anuladas: number;
    }>("/api/student/exam-end", {
      method: "POST",
      body: JSON.stringify({ examId })
    }),
  // ===== Avaliação composta da disciplina (read-only) =====
  getDisciplinaEvaluation: (disciplinaId: string) =>
    call<StudentDisciplinaEvaluation>(
      `/api/student/disciplina-evaluation?disciplinaId=${encodeURIComponent(
        disciplinaId
      )}`
    ),
  // ===== Estudando com IA =====
  listAiStudy: () =>
    call<{ sessions: AiStudySessionRow[] }>("/api/student/ai-study"),
  getAiStudy: (id: string) =>
    call<AiStudySessionDetail>(`/api/student/ai-study?id=${encodeURIComponent(id)}`),
  createAiStudy: (data: {
    theme: string;
    title?: string;
    kind?: "chat" | "essay";
    essayMeta?: Partial<EssayMeta>;
    /** Deeplink reverso banco→caderno: pré-anexa bancos como contexto da IA
     *  já na criação. Backend re-valida acesso e ignora IDs sem permissão.
     *  [[project-caderno-bank-context]] */
    linkedBankIds?: string[];
  }) =>
    call<{ id: string; kind: string; linkedBankIds?: string[] }>("/api/student/ai-study", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  patchEssayMeta: (id: string, meta: Partial<EssayMeta>) =>
    call<{ ok: true }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&action=essay_meta`,
      { method: "PATCH", body: JSON.stringify(meta) }
    ),
  addEssayFragment: (
    id: string,
    data: {
      /** Mantido por compat — o backend deriva de quais campos vierem. */
      kind?: "text" | "image" | "table" | "composite";
      label?: string;
      textContent?: string;
      imageDataUrl?: string;
      tableData?: { headers: string[]; rows: string[][] };
    }
  ) =>
    call<{ id: string }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&action=fragment`,
      { method: "POST", body: JSON.stringify(data) }
    ),
  deleteEssayFragment: (id: string, fragmentId: string) =>
    call<{ deleted: string }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&action=fragment&fragmentId=${encodeURIComponent(fragmentId)}`,
      { method: "DELETE" }
    ),
  startEssay: (id: string) =>
    call<{ ok: true; essayMeta: EssayMeta }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&action=start`,
      { method: "POST", body: "{}" }
    ),
  pauseEssay: (id: string) =>
    call<{ ok: true; essayMeta: EssayMeta }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&action=pause`,
      { method: "POST", body: "{}" }
    ),
  resumeEssay: (id: string) =>
    call<{ ok: true; essayMeta: EssayMeta }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&action=resume`,
      { method: "POST", body: "{}" }
    ),
  submitEssay: (id: string, content: string) =>
    call<{ ok: true; grading: EssayGrading }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&action=submit_essay`,
      { method: "POST", body: JSON.stringify({ content }) }
    ),
  autosaveEssayContent: (id: string, content: string) =>
    call<{ ok: true; savedAt: string }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&action=essay_content`,
      { method: "PATCH", body: JSON.stringify({ content }) }
    ),
  duplicateEssay: (id: string) =>
    call<{ id: string }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&action=duplicate`,
      { method: "POST", body: "{}" }
    ),
  generateEssayProposal: (data: {
    category: string;
    themeHint?: string | null;
    fragmentCount: number;
    banca?: string | null;
  }) =>
    call<{ id: string; generated: true }>(
      "/api/student/ai-study?action=generate_essay",
      { method: "POST", body: JSON.stringify(data) }
    ),
  // ============================================
  // Biblioteca de Temas de Redação (aluno)
  // [[project-essay-themes-library]]
  // ============================================
  listEssayThemesLibrary: () =>
    call<{
      themes: StudentEssayThemeListItem[];
      groups: Array<{ examEditionKey: string; themes: StudentEssayThemeListItem[] }>;
      ungrouped: StudentEssayThemeListItem[];
      /** Pastas da Biblioteca que têm pelo menos 1 essay_theme acessível pro
       *  aluno. UI usa pra criar tabs/chips de filtro por banca. */
      folders: StudentEssayFolder[];
    }>("/api/student/essay-themes"),
  getEssayThemeLibrary: (id: string) =>
    call<StudentEssayThemeFull>(
      `/api/student/essay-themes?id=${encodeURIComponent(id)}`
    ),
  applyEssayThemeLibrary: (id: string) =>
    call<{ ok: true; sessionId: string }>(
      `/api/student/essay-themes?id=${encodeURIComponent(id)}&action=apply`,
      { method: "POST", body: "{}" }
    ),
  sendAiStudyMessage: (
    id: string,
    message: string,
    opts: { verbosity?: "concise" | "standard" | "detailed" } = {}
  ) =>
    call<{ reply: string; provider: string; model: string }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&action=message`,
      {
        method: "POST",
        body: JSON.stringify({ message, ...(opts.verbosity ? { verbosity: opts.verbosity } : {}) })
      }
    ),
  /** Substitui (idempotente) o conjunto de bancos anexados como contexto da
   *  IA do caderno. Backend re-valida acesso e devolve só os efetivamente
   *  aplicados — bancos sem acesso voltam em `rejected`.
   *  [[project-caderno-bank-context]] */
  attachAiStudyBanks: (id: string, bankIds: string[]) =>
    call<{ linkedBanks: LinkedBankSummary[]; rejected: string[] }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&action=attach_banks`,
      { method: "PATCH", body: JSON.stringify({ bankIds }) }
    ),
  /** Adiciona UM banco a um caderno existente (preserva os outros já
   *  anexados). Usado pelo fluxo "Estudar este banco com IA → adicionar
   *  a caderno existente". */
  addAiStudyBank: (id: string, bankId: string) =>
    call<{ linkedBanks: LinkedBankSummary[]; alreadyAttached: boolean }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&action=add_bank`,
      { method: "PATCH", body: JSON.stringify({ bankId }) }
    ),
  detachAiStudyBank: (id: string, bankId: string) =>
    call<{ linkedBanks: LinkedBankSummary[] }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&action=detach_bank`,
      { method: "PATCH", body: JSON.stringify({ bankId }) }
    ),
  uploadAiStudyDoc: (
    id: string,
    data: {
      filename: string;
      mimeType: string;
      sizeBytes: number;
      extractedText: string;
    }
  ) =>
    call<{
      id: string;
      storage?: { usedBytes: number; quotaBytes: number };
    }>(`/api/student/ai-study?id=${encodeURIComponent(id)}&action=upload`, {
      method: "POST",
      body: JSON.stringify(data)
    }),
  patchAiStudy: (id: string, data: { title?: string; status?: "active" | "archived" }) =>
    call<{ ok: true }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(data) }
    ),
  archiveAiStudy: (id: string) =>
    call<{ archived: string }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  hardDeleteAiStudy: (id: string) =>
    call<{ deleted: string; hard: true }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&hard=true`,
      { method: "DELETE" }
    ),
  renameAiStudy: (id: string, title: string) =>
    call<{ ok: true }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify({ title }) }
    ),
  retitleAiStudyTheme: (id: string, theme: string) =>
    call<{ ok: true }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify({ theme }) }
    ),
  unarchiveAiStudy: (id: string) =>
    call<{ ok: true }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify({ status: "active" }) }
    ),
  deleteAiStudyDoc: (sessionId: string, docId: string) =>
    call<{ deleted: string }>(
      `/api/student/ai-study?id=${encodeURIComponent(sessionId)}&docId=${encodeURIComponent(docId)}`,
      { method: "DELETE" }
    ),
  // ===== Caderno: análises (briefing/summary/glossary/study_guide/faq/audio_overview) =====
  // [[project-caderno-notebooklm-roadmap]] Fase 1.
  listAiStudyAnalyses: (id: string) =>
    call<{
      analyses: Array<{
        id: string;
        kind: string;
        content: string;
        audioDataUrl: string | null;
        model: string | null;
        provider: string | null;
        createdAt: string;
        updatedAt: string;
      }>;
    }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&action=analyses`
    ),
  generateAiStudyAnalysis: (
    id: string,
    kind: "briefing" | "summary" | "glossary" | "study_guide" | "faq"
  ) =>
    call<{
      id: string;
      kind: string;
      content: string;
      provider: string;
      model: string;
    }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&action=generate_analysis`,
      { method: "POST", body: JSON.stringify({ kind }) }
    ),
  generateAiStudyAudioOverview: (id: string) =>
    call<{
      id: string;
      kind: "audio_overview";
      content: string;
      audioDataUrl: string;
      sizeBytes: number;
    }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&action=audio_overview`,
      { method: "POST", body: "{}" }
    ),
  ingestAiStudyUrl: (id: string, url: string) =>
    call<{
      id: string;
      filename: string;
      sourceUrl: string;
      textLength: number;
      lastSnapshotAt: string;
    }>(
      `/api/student/ai-study?id=${encodeURIComponent(id)}&action=ingest_url`,
      { method: "POST", body: JSON.stringify({ url }) }
    ),
  /** Re-captura o snapshot de uma fonte URL existente. Rate limit:
   *  1 sucesso por 24h por fonte. Quando falha, o servidor devolve 429 com
   *  `details.nextAllowedAt` (string ISO). */
  refreshAiStudyUrl: (sessionId: string, docId: string) =>
    call<{
      id: string;
      filename: string;
      sourceUrl: string;
      sizeBytes: number;
      diffBytes: number;
      lastSnapshotAt: string;
      nextAllowedAt: string;
    }>(
      `/api/student/ai-study?id=${encodeURIComponent(sessionId)}&action=refresh_url&docId=${encodeURIComponent(docId)}`,
      { method: "POST", body: "{}" }
    ),
  // ===== Pagamento (cartões + assinaturas) =====
  listPaymentMethods: () =>
    call<{ items: PaymentMethodRow[] }>("/api/student/payment-methods"),
  addPaymentMethod: (data: {
    number: string;
    holderName: string;
    holderDoc: string;
    cvv: string;
    expMonth: number;
    expYear: number;
    billingAddress: {
      zip: string;
      street: string;
      number: string;
      complement?: string;
      neighborhood?: string;
      city: string;
      state: string;
    };
    asPriority: "primary" | "backup";
  }) =>
    call<{ method: PaymentMethodRow }>("/api/student/payment-methods", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  promotePaymentMethod: (id: string, promoteTo: "primary" | "backup") =>
    call<{ ok: true }>("/api/student/payment-methods", {
      method: "PATCH",
      body: JSON.stringify({ id, promoteTo })
    }),
  removePaymentMethod: (id: string) =>
    call<{ ok: true }>(
      `/api/student/payment-methods?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  listSubscriptions: () =>
    call<{ items: SubscriptionRow[] }>("/api/student/subscriptions"),
  updateSubscription: (data: {
    id: string;
    status?: "active" | "paused";
    billingType?: "pix_monthly" | "credit_card";
  }) =>
    call<{ subscription: SubscriptionRow }>("/api/student/subscriptions", {
      method: "PATCH",
      body: JSON.stringify(data)
    }),
  cancelSubscription: (id: string) =>
    call<{ ok: true }>(
      `/api/student/subscriptions?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),

  // ============================================
  // Gravador de aulas — captura local + transcrição/resumo no server
  // ============================================
  listLectures: () =>
    call<{ lectures: LectureRow[] }>("/api/student/lectures"),
  getLecture: (id: string) =>
    call<{ lecture: LectureRow }>(
      `/api/student/lectures?id=${encodeURIComponent(id)}`
    ),
  createLecture: (data: CreateLectureInput) =>
    call<{ lecture: LectureRow }>("/api/student/lectures", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  updateLecture: (id: string, data: Partial<CreateLectureInput>) =>
    call<{ lecture: LectureRow }>(
      `/api/student/lectures?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(data) }
    ),
  /** Edita transcript/summary diretamente — usado pelo rename de speaker que
   *  faz find/replace global em ambos os campos. */
  updateLectureText: (id: string, transcript: string, summary: string) =>
    call<{ lecture: LectureRow }>(
      `/api/student/lectures?id=${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        body: JSON.stringify({ transcript, summary })
      }
    ),
  deleteLecture: (id: string) =>
    call<{ ok: true }>(
      `/api/student/lectures?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  /** Sobe blob de áudio (multipart) e dispara transcrição+resumo via Gemini.
   *  Retorna persona detectada + se usou BYOK. status final da gravação volta
   *  pra 'ready'. Cobrança de cota só rola quando NÃO é BYOK. */
  transcribeLecture: async (id: string, audio: Blob, durationSeconds: number) => {
    const fd = new FormData();
    fd.append("audio", audio, "lecture.webm");
    fd.append("durationSeconds", String(Math.floor(durationSeconds)));
    const res = await fetch(
      `/api/student/lectures/transcribe?id=${encodeURIComponent(id)}`,
      { method: "POST", credentials: "include", body: fd }
    );
    let body: any = null;
    try { body = await res.json(); } catch { /* ignore */ }
    if (!res.ok) {
      const code = body?.error?.code ?? `http_${res.status}`;
      const msg = body?.error?.message ?? `Falha ${res.status}`;
      throw new ApiError(code, msg, res.status);
    }
    return body as {
      ok: true;
      lectureId: string;
      persona: { id: string; label: string; color: string };
      usedBYOK: boolean;
      durationSeconds: number;
    };
  },

  // ===== QuickStudy (tira-dúvida rápido) =====
  // [[project-quickstudy]]
  getQuickStudyLimits: () =>
    call<{ limits: QuickStudyLimits }>("/api/public/quick-study-limits"),
  listQuickStudyConversations: (archived = false) =>
    call<{ conversations: QuickStudyConversation[] }>(
      `/api/student/quick-study/conversations${archived ? "?archived=1" : ""}`
    ),
  createQuickStudyConversation: () =>
    call<{ conversation: QuickStudyConversation }>(
      "/api/student/quick-study/conversations",
      { method: "POST" }
    ),
  patchQuickStudyConversation: (id: string, patch: { title?: string; archived?: boolean }) =>
    call<{ conversation: QuickStudyConversation }>(
      `/api/student/quick-study/conversations?id=${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(patch) }
    ),
  deleteQuickStudyConversation: (id: string) =>
    call<{ ok: true }>(
      `/api/student/quick-study/conversations?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  getQuickStudyMessages: (conversationId: string) =>
    call<{
      conversation: QuickStudyConversation;
      messages: QuickStudyMessage[];
    }>(
      `/api/student/quick-study/messages?conversationId=${encodeURIComponent(conversationId)}`
    ),
  sendQuickStudyMessage: (
    conversationId: string,
    payload: { text: string; images?: QuickStudyImageInput[] }
  ) =>
    call<{
      userMessage: QuickStudyMessage;
      assistantMessage: QuickStudyMessage | null;
      conversation: QuickStudyConversation;
      refusedReason: string | null;
    }>(
      `/api/student/quick-study/messages?conversationId=${encodeURIComponent(conversationId)}`,
      { method: "POST", body: JSON.stringify(payload) }
    ),
  deleteQuickStudyMessage: (conversationId: string, messageId: string) =>
    call<{ ok: true; deletedId: string }>(
      `/api/student/quick-study/messages?conversationId=${encodeURIComponent(
        conversationId
      )}&messageId=${encodeURIComponent(messageId)}`,
      { method: "DELETE" }
    ),

  // ===== Compartilhamentos =====
  listShares: (box: "incoming" | "outgoing") =>
    call<{ shares: ShareRow[] }>(
      `/api/student/shares?box=${box}`
    ),
  createShare: (data: {
    kind: "bank" | "caderno" | "lecture" | "nota";
    resourceId: string;
    recipientEmails: string[];
    message?: string;
  }) =>
    call<{ ok: true; count: number }>("/api/student/shares", {
      method: "POST",
      body: JSON.stringify(data)
    }),
  acceptShare: (id: string) =>
    call<{ ok: true; acceptedResourceId: string | null }>(
      `/api/student/shares?id=${encodeURIComponent(id)}&action=accept`,
      { method: "POST" }
    ),
  rejectShare: (id: string) =>
    call<{ ok: true }>(
      `/api/student/shares?id=${encodeURIComponent(id)}&action=reject`,
      { method: "POST" }
    ),
  cancelShare: (id: string) =>
    call<{ ok: true }>(
      `/api/student/shares?id=${encodeURIComponent(id)}&action=cancel`,
      { method: "POST" }
    ),
  revokeShare: (id: string) =>
    call<{ ok: true }>(
      `/api/student/shares?id=${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),
  // ===== Créditos pré-pago — [[project-prepaid-credits-strategy]] =====
  getMyCredits: () =>
    call<{
      balanceCents: number;
      totalToppedUpCents: number;
      totalConsumedCents: number;
      ledger: Array<{
        id: string;
        deltaCents: number;
        kind: string;
        description: string | null;
        createdAt: string;
      }>;
    }>(`/api/student/credits`)
};

export interface ShareRow {
  id: string;
  kind: "bank" | "caderno" | "lecture" | "nota";
  resourceId: string;
  ownerUserId: string;
  ownerName: string | null;
  ownerEmail: string | null;
  recipientEmailNormalized: string;
  message: string | null;
  status:
    | "pending"
    | "accepted"
    | "rejected"
    | "revoked"
    | "canceled_by_recipient";
  acceptedResourceId: string | null;
  createdAt: string;
  respondedAt: string | null;
  resourceTitle: string | null;
}

// ============================================
// Tipos do Gravador de Aulas
// ============================================
export type LectureKind = "aula" | "palestra" | "reuniao" | "entrevista" | "estudo" | "outro";
export type LectureFocus = "detailed_notes" | "executive" | "glossary" | "exam_questions";
export type LectureStatus = "draft" | "processing" | "ready" | "failed";
export type LectureCaptureKind = "audio_only" | "screen_and_audio";

export interface LectureRow {
  id: string;
  title: string;
  kind: LectureKind;
  disciplinaId: string | null;
  theme: string | null;
  level: string | null;
  language: string;
  summaryFocus: LectureFocus;
  expectedSpeakers: number | null;
  mainSpeakerName: string | null;
  userNotes: string | null;
  tags: string[];
  captureKind: LectureCaptureKind;
  audioSources: string[];
  durationSeconds: number;
  sizeBytes: number;
  status: LectureStatus;
  transcript: string | null;
  summary: string | null;
  aiProvider: string | null;
  aiModel: string | null;
  processError: string | null;
  linkedAiStudySessionId: string | null;
  sourceLectureId: string | null;
  /** Nome do aluno que compartilhou essa gravação (null = própria). */
  sharedByName: string | null;
  createdAt: string;
  processedAt: string | null;
}

export interface CreateLectureInput {
  title: string;
  kind: LectureKind;
  disciplinaId?: string | null;
  theme?: string;
  level?: string;
  language?: string;
  summaryFocus?: LectureFocus;
  expectedSpeakers?: number;
  mainSpeakerName?: string;
  userNotes?: string;
  tags?: string[];
  captureKind?: LectureCaptureKind;
  audioSources?: string[];
  durationSeconds?: number;
  sizeBytes?: number;
  linkedAiStudySessionId?: string | null;
}

// ===== QuickStudy types =====

export interface QuickStudyLimits {
  enabled: boolean;
  maxImagesPerMessage: number;
  maxImageSizeMb: number;
  maxTextChars: number;
}

export interface QuickStudyConversation {
  id: string;
  title: string;
  personaId: string | null;
  personaLabel: string | null;
  personaColor: string | null;
  channel: string;
  createdAt: string;
  lastMessageAt: string;
  messageCount: number;
  archived: boolean;
}

export interface QuickStudyMessage {
  id: string;
  role: string; // 'user' | 'assistant'
  content: string;
  createdAt: string;
  model: string | null;
  refused: boolean;
  images: Array<{ mimeType: string; base64: string; sizeBytes: number }> | null;
}

export interface QuickStudyImageInput {
  mimeType: string;
  base64: string;
  sizeBytes: number;
}

export interface PaymentMethodRow {
  id: string;
  userId: string;
  kind: "pix" | "credit_card";
  provider: "pix_manual" | "stripe" | "mercadopago" | "mock";
  brand: string | null;
  last4: string | null;
  holderName: string | null;
  expMonth: number | null;
  expYear: number | null;
  priority: number;
  addedAt: string;
}

export interface SubscriptionChargeRow {
  id: string;
  status: "pending" | "succeeded" | "failed" | "refunded";
  provider: "pix_manual" | "stripe" | "mercadopago" | "mock";
  valorCents: number;
  failureReason: string | null;
  methodPriority: number | null;
  chargedAt: string;
}

export interface SubscriptionRow {
  id: string;
  pacoteId: string | null;
  pacoteNomeSnap: string;
  valorCents: number;
  billingType: "pix_monthly" | "credit_card";
  status: "active" | "paused" | "past_due" | "canceled";
  nextChargeAt: string;
  lastChargeAt: string | null;
  failedAttempts: number;
  createdAt: string;
  recentCharges: SubscriptionChargeRow[];
}

export interface AiStudySessionRow {
  id: string;
  kind: "chat" | "essay";
  theme: string;
  title: string;
  status: string;
  essayPhase?: "setup" | "running" | "paused" | "submitted" | null;
  essayScore?: number | null;
  essayStatus?: string | null;
  documentsCount?: number;
  /** Nome do aluno dono do caderno-fonte (null = caderno próprio). */
  sharedByName?: string | null;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface EssayMeta {
  banca?: string | null;
  customPersona?: string | null;
  textosApoio?: string | null;
  direcionadores?: string | null;
  timerMode?: "none" | "countdown";
  allowPause?: boolean;
  durationMin?: number | null;
  maxKind?: "words" | "chars" | "lines";
  maxValue?: number | null;
  // Linhas equivalentes manuscritas
  linePreset?: "enem" | "fuvest" | "unicamp" | "unesp" | "ita" | "custom" | "free";
  maxLines?: number | null;
  charsPerLineBase?: number | null;
  handwritingSize?: "tiny" | "small" | "medium" | "large" | "huge" | "calibrated";
  calibratedCharsPerLine?: number | null;
  // Configurações visuais do caderno realista [[project-caderno-realista]]
  /** Chars por linha efetivos (clamp 30-130). Sobrescreve charsPerLineBase. */
  cadernoCharsPerLine?: number | null;
  /** [Deprecated MVP] Modo de quebra de linha. Mantido por compat — apenas
   *  "free" é suportado. */
  cadernoHyphenMode?: "free";
  /** Mostra numeração à esquerda das linhas */
  cadernoShowLineNumbers?: boolean;
  /** Estilo da fonte do texto do aluno */
  cadernoFontStyle?: "inter" | "handwriting" | "mono";
  /** Modo de visualização: lateral, modal, ou janela popup */
  cadernoView?: "side" | "modal" | "popout";
  /** Idioma em que a redação está sendo escrita — afeta silabador (modo
   *  hífen) e instrução pro corretor. Default "pt". */
  cadernoLanguage?: "pt" | "en" | "es";
  /** Idiomas que o corretor ACEITA. Se aluno escrever em idioma fora dessa
   *  lista, recebe nota zero por "fuga de idioma" (regra ENEM/banca).
   *  Default ["pt"]. */
  acceptedLanguages?: Array<"pt" | "en" | "es">;
  /** Exige título (linha 0 separada)? FUVEST exige; ENEM não. */
  cadernoRequireTitle?: boolean;
  /** Quando true, o requireTitle foi IMPOSTO pelo tema da biblioteca
   *  (vestibular exige por norma da prova) — UI trava o toggle. */
  themeImposedTitle?: boolean;
  /** Título da redação (linha 0, limitada a charsPerLine, sempre 1 linha). */
  essayTitle?: string;
  /** Assistente de parágrafo: insere indentação automaticamente após Enter */
  cadernoAutoParagraph?: boolean;
  /** Indentação usada (chars de espaço) quando auto-paragraph está ON OU
   *  quando aluno clica "Inserir parágrafo aqui" */
  cadernoParagraphIndent?: number;
  /** Penalização do corretor:
   *  - "spelling": tira nota por erro ortográfico
   *  - "paragraphs": tira nota por ausência de parágrafos
   *  - "language": tira nota por idioma fora dos aceitos (já zera, mas
   *    flag explícito permite desligar pra simulações casuais)
   */
  cadernoPenalties?: {
    spelling?: boolean;
    paragraphs?: boolean;
    language?: boolean;
  };
  // Lifecycle
  startedAt?: string | null;
  pausedAt?: string | null;
  totalPausedMs?: number;
  submittedAt?: string | null;
  // Vindos da Biblioteca de Redação quando o aluno aplicou um tema oficial.
  // [[project-essay-themes-library]]
  year?: number | null;
  phase?: string | null;
  edition?: string | null;
  applicationDate?: string | null;
  examEditionKey?: string | null;
  statement?: string | null;
  officialInstructions?: string | null;
  directives?: string[] | null;
  originalSourceUrl?: string | null;
  fromThemeSlug?: string | null;
}

export interface EssayFragment {
  id: string;
  /** "composite" = combina dois ou mais (texto/imagem/tabela). Os tipos únicos
   *  ("text", "image", "table") são mantidos por compat com fragmentos antigos. */
  kind: "text" | "image" | "table" | "composite";
  label: string;
  textContent: string;
  imageDataUrl: string | null;
  tableData: { headers: string[]; rows: string[][] } | null;
  position: number;
  createdAt: string;
  /** Fonte específica do fragmento (markdown). Migration 0050. */
  source?: string;
  /** "real" | "ai_generated" | "unknown". Migration 0050. */
  sourceKind?: "real" | "ai_generated" | "unknown";
}

export interface EssayGrading {
  content: string;
  /** Conteúdo sem a linha header "NOTA_FINAL: ...". Sessões antigas: use content. */
  body?: string;
  /** Nota normalizada 0.00–10.00 extraída da resposta da IA. null se não veio. */
  score?: number | null;
  /** EXCELENTE | APROVADO | REGULAR | INSUFICIENTE. */
  status?: string | null;
  provider: string;
  model: string;
  gradedAt: string;
}
export interface AiStudyMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
}
export interface AiStudyDocument {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  extractedText: string;
  /** "upload" (arquivo do disco) ou "url" (página web fetched). Default "upload". */
  sourceKind?: "upload" | "url";
  /** URL original quando sourceKind="url". */
  sourceUrl?: string | null;
  uploadedAt: string;
  /** Último snapshot COM SUCESSO. URL-only. ISO string. */
  lastSnapshotAt?: string | null;
  /** Última tentativa de refresh (sucesso ou erro). URL-only. */
  lastAttemptAt?: string | null;
  /** Mensagem da última falha de refresh. null/undefined = última OK. */
  lastError?: string | null;
}
export interface LinkedBankSummary {
  id: string;
  name: string;
  questionCount: number;
}

export interface AiStudySessionDetail {
  session: AiStudySessionRow & {
    essayMeta: EssayMeta | null;
    essayContent: string | null;
    essayGrading: EssayGrading | null;
    /** Bancos anexados ao caderno como contexto da IA. Sempre presente
     *  (vazio quando nada anexado). [[project-caderno-bank-context]] */
    linkedBanks: LinkedBankSummary[];
  };
  persona?: AiPersona | null;
  messages: AiStudyMessage[];
  documents: AiStudyDocument[];
  fragments?: EssayFragment[];
  /** Simulados gerados POR ESSA sessão via "Gerar simulado a partir do
   *  caderno". Permite o aluno reabrir os bancos criados sem ir até o
   *  acervo. Ordem: mais recente primeiro. */
  generatedBanks?: Array<{
    id: string;
    name: string;
    questionCount: number;
    createdAt: string;
  }>;
  /** Cota de armazenamento do usuário (agregada de TODOS os cadernos dele).
   *  Front mostra "X MB / Y MB" no upload. [[project-caderno-storage-quota]] */
  storage?: {
    usedBytes: number;
    quotaBytes: number;
    remainingBytes: number;
  };
}

export interface UserWebhookSubscription {
  id: string;
  url: string;
  secretPreview?: string;
  events: string[];
  active: boolean;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserWebhookEventLog {
  id: string;
  subscriptionId: string;
  kind: string;
  status: "pending" | "delivered" | "failed";
  attempts: number;
  lastStatusCode: number | null;
  lastResponseMs: number | null;
  lastError: string | null;
  createdAt: string;
  deliveredAt: string | null;
  lastAttemptAt: string | null;
}

export interface AiPersona {
  id: string;
  label: string;
  icon: string;
  systemDescription: string;
  color:
    | "emerald"
    | "sky"
    | "violet"
    | "amber"
    | "rose"
    | "teal"
    | "blue"
    | "indigo";
}

export interface AiChatTurn {
  role: "user" | "assistant";
  content: string;
}

export interface AiChatRequest {
  /** Quando presente, backend persiste a conversa por questão em
   *  bank_progress.data.aiChats[qid]. Sem bankId, fica só na memória do
   *  cliente. */
  bankId?: string;
  question: {
    id?: string;
    tema?: string | null;
    fonte?: string | null;
    pergunta?: string | null;
    caso?: string | null;
    alternativas?: unknown;
    correta?: number | null;
    resolucao?: string | null;
    anulada?: boolean;
  };
  respostaAluno?: number | null;
  history?: AiChatTurn[];
  message: string;
  /** Override por sessão. Se ausente, backend usa pref persistida. */
  verbosity?: "concise" | "standard" | "detailed";
  provider?: "gemini" | "openai" | "anthropic" | "xai" | "deepseek";
  model?: string;
  modelCatalogId?: string;
  accessMode?: "platform" | "byok" | "connected";
  byok?: {
    provider: "gemini" | "openai" | "anthropic" | "xai" | "deepseek";
    apiKey: string;
    model?: string;
  };
}

export interface AiChatResponse {
  reply: string;
  persona: AiPersona;
  providerUsed: string;
  modelUsed?: string;
  modelKindUsed?: "general" | "reasoning" | "coding";
  fallbackUsed?: boolean;
  fallbackReason?: string;
}

export interface AiValidateKeyResponse {
  ok: boolean;
  provider?: "gemini" | "openai" | "anthropic" | "xai" | "deepseek";
  model?: string;
  status?: number;
  error?: string;
}

export interface AiModelCatalogItem {
  id: string;
  value: string;
  label: string;
  description: string;
  kind: "general" | "reasoning" | "coding";
  recommended?: boolean;
  freeFriendly?: boolean;
}

export interface AiProviderCatalog {
  label: string;
  keyUrl: string;
  keyLabel: string;
  connectionModes: {
    platform: boolean;
    byok: boolean;
    connectedAccount: boolean;
  };
  connectionNote?: string;
  models: AiModelCatalogItem[];
}

export interface AiCatalogResponse {
  providers: Record<"gemini" | "openai" | "anthropic" | "xai" | "deepseek", AiProviderCatalog>;
  strategy: {
    supportedNow: string[];
    planned: string[];
    note: string;
  };
}

export interface AiConnectionRow {
  provider: "gemini" | "openai" | "anthropic" | "xai" | "deepseek";
  label: string;
  status: "disconnected" | "connected" | "planned";
  availableNow: boolean;
  canStartOAuth: boolean;
  interested: boolean;
  message: string;
}

export interface AiConnectionsResponse {
  supportedNow: string[];
  planned: string[];
  connections: AiConnectionRow[];
}

export interface AiConnectionsUpdateResponse {
  ok: true;
  interestedProviders: Array<"gemini" | "openai" | "anthropic" | "xai" | "deepseek">;
}

// ===== Matérias / Disciplinas do aluno =====
export interface StudentDisciplinaRow {
  id: string;
  name: string;
  code: string | null;
  description?: string | null;
  status: "draft" | "active" | "closed";
  startsAt: string | null;
  endsAt: string | null;
  offering: { id: string; turmaId: string; turmaName: string; turmaCode: string | null } | null;
  teachers?: Array<{ userId: string; name: string; email: string }>;
}

export interface StudentDisciplinaCompraRow {
  id: string;
  pacoteNomeSnap: string;
  numAulasSnap: number;
  valorCents: number;
  status: "pending" | "paid" | "expired" | "canceled";
  createdAt: string;
  paidAt: string | null;
  canceledAt: string | null;
}

export interface StudentDisciplinaAulaRow {
  id: string;
  professorUserId: string;
  professorName: string;
  dataHora: string;
  duracaoMin: number;
  status: "scheduled" | "realized" | "canceled" | "no_show";
  creditosConsumidos: number;
  descricao: string | null;
  canceledReason: string | null;
  realizedAt: string | null;
}

export interface StudentDisciplinaDetail {
  id: string;
  name: string;
  code: string | null;
  status: "draft" | "active" | "closed";
  gradingEnabled: boolean;
  startsAt: string | null;
  endsAt: string | null;
  offering: { id: string; turmaId: string; turmaName: string; turmaCode: string | null };
  enrollment: { enrolledAt: string } | null;
  saldo: number;
  compras: StudentDisciplinaCompraRow[];
  aulas: StudentDisciplinaAulaRow[];
  teachers: Array<{ userId: string; name: string; email: string }>;
  banks: Array<{ id: string; name: string }>;
  contents: any[];
  assessments: Array<{
    id: string;
    kind: "prova" | "trabalho" | "simulada";
    title: string;
    startsAt: string | null;
    endsAt: string | null;
    releasedAt: string | null;
    status: string;
    myAttempt: { submittedAt: string | null; score: number | null } | null;
  }>;
}

// ===== Pacotes (visão aluno) =====
export interface PacoteAvailable {
  id: string;
  disciplinaId: string;
  nome: string;
  descricao: string | null;
  numAulas: number;
  precoCents: number;
  ordem: number;
}

// ===== Notificações =====
export type NotificationKind =
  | "payment_received"
  | "payment_pending"
  | "lesson_scheduled"
  | "lesson_logged"
  | "lesson_canceled"
  | "lesson_reminder"
  | "credit_low"
  | "credit_zero"
  | "enrolled_in_offering"
  | "assigned_to_disciplina"
  | "system"
  | "content_added"
  | "message_received"
  | "ai_bank_generated"
  | "ai_bank_failed"
  | "study_group_invite"
  | "study_group_content_added"
  | "study_group_role_changed";

export interface NotificationRow {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string | null;
  payload: Record<string, unknown> | null;
  linkUrl: string | null;
  readAt: string | null;
  hiddenAt: string | null;
  createdAt: string;
}

// ===== Minha Conta =====
export interface AccountInfo {
  id: string;
  email: string;
  name: string;
  /** Nome social opcional. Quando preenchido, substitui `name` em tudo que
   *  é exibido pra outros usuários (chat, recibos, perfis). LGBTQ+ friendly. */
  socialName: string | null;
  /** Iniciais customizadas (máx 4 chars). Quando null, deriva automaticamente
   *  do nome efetivo (socialName > name) pegando primeiro+último token. */
  initialsOverride: string | null;
  apelido: string | null;
  phone: string | null;
  /** Prefs JSON merge-friendly. Tem chaves variadas (homeLayout,
   *  studentProfile, etc) — manter generic e fazer narrow nos consumers. */
  prefs: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

// ===== Lembretes =====
export type ReminderChannel = "email" | "sms" | "whatsapp" | "push";
export interface ReminderPref {
  id: string;
  userId: string;
  offsetMinutes: number;
  channel: ReminderChannel;
  ativo: boolean;
  createdAt: string;
}

// ===== Aulas (visão aluno) =====
export interface AulaStudentRow {
  id: string;
  offeringId: string;
  professorUserId: string;
  professorName: string;
  dataHora: string;
  duracaoMin: number;
  status: "scheduled" | "realized" | "canceled" | "no_show";
  creditosConsumidos: number;
  descricao: string | null;
  recorrenciaGrupoId: string | null;
  realizedAt: string | null;
  canceledAt: string | null;
  canceledReason: string | null;
  disciplinaId: string;
  disciplinaName: string;
}

// ===== Compras =====
/** Branding da organização vinculada à compra. Usado pra renderizar o recibo
 *  com a identidade visual da escola (logo, cor). Vem em getCompra(id). */
export interface CompraCompanyBranding {
  id: string;
  name: string;
  branding: {
    logoUrl: string | null;
    colorPrimary: string | null;
    colorSecondary: string | null;
    fontFamily: string | null;
  };
}

export interface CompraRow {
  id: string;
  offeringId: string;
  alunoUserId: string;
  pacoteId: string | null;
  pacoteNomeSnap: string;
  numAulasSnap: number;
  valorCents: number;
  txid: string;
  pixBrcode: string;
  e2eId: string | null;
  pspProvider: string;
  status: "pending" | "paid" | "expired" | "canceled";
  createdAt: string;
  paidAt: string | null;
  canceledAt: string | null;
  expiresAt: string | null;
}

// ===== Mensagens =====
export interface InboxMessageRow {
  id: string;
  offeringId: string;
  fromUserId: string;
  fromName: string;
  title: string;
  body: string;
  toUserId: string | null;
  createdAt: string;
  readAt: string | null;
}

// ===== Passkeys =====
export interface PasskeyDevice {
  id: string;
  name: string;
  autoLabel: string;
  browser: string | null;
  os: string | null;
  model: string | null;
  platform: string | null;
  platformVersion: string | null;
  deviceType: string | null;
  backedUp: boolean;
  transports: string[];
  createdAt: string;
  lastUsedAt: string | null;
}

// ===== Conteúdos =====
export interface ContentItem {
  id: string;
  offeringId: string;
  parentId: string | null;
  kind: "folder" | "file";
  name: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

// ===== Provas agendadas =====

export type ScheduledExamPhase = "lobby" | "running" | "closed" | "released";

export interface StudentExamRow {
  id: string;
  disciplinaName: string;
  title: string;
  description: string | null;
  startsAt: string | null;
  endsAt: string | null;
  durationMinutes: number | null;
  releasedAt: string | null;
  status: "scheduled" | "running" | "closed";
  phase: ScheduledExamPhase;
  attempt: {
    startedAt: string;
    submittedAt: string | null;
    score: string | null;
  } | null;
}

export interface ScheduledExamMeta {
  id: string;
  title: string;
  description: string | null;
  subject: string | null;
  instructions: string | null;
  startsAt: string | null;
  endsAt: string | null;
  durationMinutes: number | null;
  releasedAt: string | null;
  status: "scheduled" | "running" | "closed";
  weights: number[] | null;
  maxScore: string;
  showQuestionCount: boolean;
  showWeights: boolean;
  passingGrade: number;
  disclosure: {
    level: "full" | "score" | "none";
    showsGabarito: boolean;
  } | null;
  lobbyOpen: boolean;
  /** "live" = Sala ao vivo (cronômetro global). "open_window" = Período aberto
   *  (aluno escolhe quando começar, cronômetro individual). */
  examMode: "live" | "open_window";
  /** Duração individual em minutos no modo open_window. null = use endsAt. */
  studentDurationMin: number | null;
  /** Aluno pode pausar/retomar (só faz sentido em open_window). */
  allowPause: boolean;
}

export interface ScheduledExamPauseMeta {
  pausedAt: string | null;
  totalPausedMs: number;
}

export interface ScheduledExamAnswer {
  opcao: number;
  tempoMs?: number;
  respondidaEm?: number;
}

export interface ScheduledExamAttemptInfo {
  startedAt: string;
  submittedAt: string | null;
  answers: Record<string, ScheduledExamAnswer | number>;
  score: string | null;
  lastHeartbeatAt: string | null;
  pauseMeta: ScheduledExamPauseMeta | null;
}

export interface StudentExamDetailResponse {
  exam: ScheduledExamMeta;
  phase: ScheduledExamPhase;
  questoes: QuestaoStudy[];
  anuladasIdx: number[];
  attempt: ScheduledExamAttemptInfo | null;
}

// ===== Avaliação composta da disciplina (visão do aluno) =====

export interface StudentDisciplinaEvaluationItem {
  id: string;
  kind: "prova" | "atividade";
  title: string;
  description: string | null;
  weight: number;
  position: number;
  assessmentId: string | null;
  assessmentTitle: string | null;
  assessmentReleased: boolean;
  score: number | null;
  comment: string | null;
  pendingReason: string | null;
}

export interface StudentDisciplinaEvaluation {
  disciplina: {
    id: string;
    name: string;
    passingGrade: number;
    recoveryGrade: number;
    recoveryEnabled: boolean;
  };
  items: StudentDisciplinaEvaluationItem[];
  finalScore: number;
  status: "aprovado" | "recuperacao" | "reprovado" | "incompleto";
  weightSum: number;
}

// ============================================
// Biblioteca de Temas de Redação (aluno) — tipos
// [[project-essay-themes-library]]
// ============================================

export interface StudentEssayThemeListItem {
  id: string;
  slug: string;
  title: string;
  displayName: string;
  banca: string;
  year: number | null;
  phase: string | null;
  edition: string | null;
  applicationDate: string | null;
  examEditionKey: string | null;
  category: string;
  difficulty: string;
  visibility: "private" | "unlisted" | "public";
  status: "draft" | "published" | "archived";
  originalSourceUrl: string | null;
  /** Pasta da Biblioteca (compartilhada com banks). null = sem pasta. */
  libraryFolderId: string | null;
}

export interface StudentEssayFolder {
  id: string;
  name: string;
  slug: string;
  /** Quantos temas dessa pasta o aluno tem acesso. */
  themeCount: number;
}

export interface StudentEssayThemeItem {
  id: string;
  kind: "text" | "image" | "table";
  content: string;
  imageDataUrl: string | null;
  tableData: { headers?: string[]; rows?: string[][]; caption?: string } | null;
  source: string;
  sourceKind: "real" | "ai_generated" | "unknown";
  position: number;
}

export interface StudentEssayThemeFragment {
  id: string;
  label: string;
  sourceOverall: string;
  note: string;
  position: number;
  items: StudentEssayThemeItem[];
}

export interface StudentEssayThemeFull extends StudentEssayThemeListItem {
  statement: string;
  officialInstructions: string;
  directives: string[];
  fragments: StudentEssayThemeFragment[];
}
